import { useState } from 'react'

type Props = {
  /** `null` quando o provedor não informa — o tipo do SDK e o do domínio
   * admitem, e a conta do Google pode não ter nome. */
  displayName: string | null
  photoURL: string | null
  /** Lado em px. O avatar é sempre quadrado e sempre redondo. */
  size: number
}

/** Foto ausente é `null` **ou** string vazia: `toMember` no adapter devolve o
 * que estiver no documento, e campo vazio é string, não nulo. */
function usablePhoto(photoURL: string | null): string | null {
  if (photoURL === null) return null
  const trimmed = photoURL.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * As iniciais de até duas palavras — "Murilo Erhardt" vira "ME", "Murilo" vira
 * "M". `null` quando não há nome nenhum de onde tirar letra, e aí quem chama
 * cai para o ícone neutro.
 *
 * `Array.from` em vez de `slice(0, 1)`: nome que começa com emoji ou com letra
 * fora do plano básico tem code unit de sobra, e `slice` cortaria meio par
 * substituto — o que aparece na tela como o losango de erro.
 */
function initials(displayName: string | null): string | null {
  if (displayName === null) return null

  const words = displayName.split(/\s+/).filter((word) => word !== '')
  const first = words[0]
  if (first === undefined) return null

  const firstLetter = Array.from(first)[0] ?? ''
  if (words.length === 1) return firstLetter.toUpperCase()

  const last = words[words.length - 1] ?? first
  const lastLetter = Array.from(last)[0] ?? ''
  return `${firstLetter}${lastLetter}`.toUpperCase()
}

/**
 * O avatar de uma pessoa, em três casos de fonte: foto, iniciais, ícone neutro.
 * Os três existem de verdade — `photoURL` é `string | null` no domínio e a
 * conta do Google pode não ter foto nem nome.
 *
 * **Decorativo de propósito.** Nada aqui tem nome acessível: o avatar sempre
 * aparece ao lado do nome da pessoa (na lista e no perfil) ou dentro de um
 * controle que já se nomeia (o link de Ajustes no Dashboard). Dar nome ao
 * avatar duplicaria essa leitura em vez de acrescentar informação.
 */
export function Avatar({ displayName, photoURL, size }: Props) {
  // A URL que já falhou, e não um booleano: assim trocar de foto volta a tentar
  // sozinho, sem efeito nenhum para reagir à mudança de prop.
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null)
  const usable = usablePhoto(photoURL)
  // Foto que não carrega cai para o degrau de baixo em vez de virar o ícone de
  // imagem quebrada do navegador. Isso não é zelo abstrato: a foto do documento
  // de membro é **por construção** a congelada no primeiro login, ou seja, a
  // URL com mais chance de já ter morrido.
  const photo = usable !== null && usable !== failedPhoto ? usable : null
  const letters = initials(displayName)
  const style = { width: `${size}px`, height: `${size}px` }
  const shape =
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ' +
    'bg-[var(--surface-sunken)] text-[var(--text-muted)]'

  if (photo !== null) {
    return (
      <img
        src={photo}
        alt=""
        style={style}
        className={`${shape} object-cover`}
        onError={() => {
          setFailedPhoto(photo)
        }}
      />
    )
  }

  if (letters !== null) {
    return (
      <span aria-hidden="true" style={style} className={`${shape} font-semibold`}>
        {letters}
      </span>
    )
  }

  return (
    <span aria-hidden="true" style={style} className={shape}>
      <svg viewBox="0 0 24 24" fill="currentColor" focusable="false" className="h-3/5 w-3/5">
        <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.4 0-8 2.4-8 5.3V22h16v-2.7c0-2.9-3.6-5.3-8-5.3Z" />
      </svg>
    </span>
  )
}
